// ResultCard.tsx - Notion 风格的结果卡片（单栏布局专用）
// 样式对齐 Result.tsx 弹窗 + 首页设计风格
import { useEffect, useRef, useState } from 'react';
import { Alert, message } from 'antd';
import { useSnapshot } from 'valtio';
import { HistoryItem, sseStore } from '@/utils/store/store';
import Markdown from 'react-markdown';
import markdownDocx, { Packer } from 'markdown-docx';
import { ResultModal } from '@/pages/AfterMeet/ResultModal';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '@/constants/messages';
import { copyToClipboard, handleError, handleSuccess } from '@/utils/helpers';
import { estimateExecutionTimeByTemplate } from '@/utils/result-time-map';

const ResultCard = ({ historyItem }: { historyItem: HistoryItem | undefined }) => {
  const [editData, setEditData] = useState<string>('');
  const [editAble, setEditAble] = useState<boolean>(false);
  const { data, loading, error, params } = useSnapshot(sseStore);
  const textareaRef = useRef<HTMLDivElement>(null);

  // 进度条相关状态
  const [progress, setProgress] = useState<number>(0);
  const [remainingTime, setRemainingTime] = useState<number>(0);

  const isNotInHistory = historyItem === undefined;

  const fData = isNotInHistory ? data : JSON.parse(historyItem?.result)['default'];

  const handleExport = async () => {
    try {
      const doc = await markdownDocx(fData);
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'document.docx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      handleError(err, ERROR_MESSAGES.DOCX_GENERATION_FAILED);
    }
  };

  const handleExportImage = () => {
    if (!fData) {
      message.info('内容为空');
      return;
    }
    ResultModal(
      {
        id: `result-${Date.now()}`,
        title: '整理结果',
        result: JSON.stringify({ default: fData }),
        createTime: Date.now(),
      },
      { autoExportImage: true },
    );
  };

  useEffect(() => {
    setEditData(data);
    if (isNotInHistory && textareaRef.current) {
      const element = textareaRef.current;
      element.scrollTop = element.scrollHeight;
    }
  }, [data]);

  /**
   * 处理进度条逻辑的useEffect
   */
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (loading && params?.Content) {
      const wordCount = params.Content.length;
      const estimatedSeconds = estimateExecutionTimeByTemplate(wordCount, params.Output_type || '');
      setRemainingTime(estimatedSeconds);
      setProgress(0);

      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + 1;
          setRemainingTime((prevTime) => Math.max(0, prevTime - 1));
          if (newProgress >= estimatedSeconds) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return estimatedSeconds;
          }
          return newProgress;
        });
      }, 1000);
    } else {
      setProgress(0);
      setRemainingTime(0);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loading, params?.Content]);

  // 没有数据且不在加载中时不渲染
  if (!loading && !fData && !error) {
    return null;
  }

  return (
    <div className="w-full flex justify-center px-6 mt-6 mb-4">
      <div
        className="w-full max-w-[720px] bg-white rounded-2xl border border-[#E9E9E7] overflow-hidden flex flex-col"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)' }}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-7 pt-6 pb-4 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <svg
              className="w-4.5 h-4.5 text-[#91918E] flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
              />
            </svg>
            <h2 className="text-base font-semibold text-[#37352F]">整理结果</h2>
          </div>
          <button
            onClick={() => window.open('https://wj.qq.com/s2/19205380/fe25/')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#91918E] hover:text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
              />
            </svg>
            内测反馈
          </button>
        </div>

        {/* 工具栏 */}
        <div className="flex items-center gap-0.5 px-7 pb-3 border-b border-[#E9E9E7] flex-shrink-0">
          <button
            disabled={loading}
            onClick={() => {
              if (!fData) {
                message.info('内容为空');
                return;
              }
              copyToClipboard(fData, SUCCESS_MESSAGES.COPY_SUCCESS);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer disabled:opacity-40 disabled:pointer-events-none"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
              />
            </svg>
            复制
          </button>
          <button
            disabled={loading}
            onClick={() => {
              if (!fData) {
                message.info('内容为空');
                return;
              }
              handleExport().then(() => handleSuccess(SUCCESS_MESSAGES.DOCX_DOWNLOAD_SUCCESS));
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer disabled:opacity-40 disabled:pointer-events-none"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
            导出 Word
          </button>
          <button
            disabled={loading}
            onClick={handleExportImage}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer disabled:opacity-40 disabled:pointer-events-none"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a2.25 2.25 0 002.25-2.25V5.25a2.25 2.25 0 00-2.25-2.25H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
              />
            </svg>
            导出图片
          </button>
          {isNotInHistory && (
            <label className={`flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#73726E] select-none rounded-lg transition-colors ${loading ? 'opacity-40 pointer-events-none' : 'cursor-pointer hover:bg-[#F7F7F5]'}`}>
              <input
                onChange={(event) => setEditAble(event.target.checked)}
                type="checkbox"
                className="toggle toggle-xs border-0 bg-[#F6F7FC] w-9"
              />
              可编辑
            </label>
          )}
        </div>

        {/* 内容区域 */}
        <div className="px-7 py-5 min-h-[480px]">
          {error && (
            <Alert
              message="错误提示"
              description={error.message}
              type="error"
              showIcon
              closable
              className="mb-4 rounded-xl"
            />
          )}

          {fData ? (
            editAble ? (
              <textarea
                className="textarea w-full min-h-[300px] text-sm text-[#37352F] leading-relaxed"
                value={editData}
                onChange={(dat) => setEditData(dat.target.value)}
              />
            ) : (
              <article
                ref={textareaRef}
                className="markdown space-y-2 text-[#37352F] text-sm leading-relaxed overflow-y-auto"
              >
                <Markdown>{fData}</Markdown>
              </article>
            )
          ) : null}

          {loading && (
            <div className={`flex flex-col items-center justify-center py-4 ${!fData ? 'pt-16 pb-8' : ''}`}>
              <span className="loading loading-dots loading-md"></span>
              {progress > 2 && (
                <div className="text-sm text-[#91918E] mt-3">
                  {Math.floor(remainingTime / 60) === 0
                    ? '整理思路中 即将完成'
                    : `整理思路中 剩余约 ${Math.floor(remainingTime / 60)} 分钟`}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultCard;
